import $ from 'jquery';
import Swal from 'sweetalert2';
import { fabric } from 'fabric';
import generateBackgroundFusion from './generateBackgroundFusion';
import {
  createBackgroundFusionMaskDataUrl,
  imageUrlToExactDataUrl,
  uploadDataUrlToUploader,
} from './backgroundFusionCanvas';
import {
  buildBackgroundFusionLayout,
  getBackgroundDescriptor,
  getScaledPersonReferenceUrl,
} from './backgroundFusionLayout';
import confirmBackgroundFusionPlacement from './confirmBackgroundFusionPlacement';
import {
  canUseBackgroundFusionSubject,
  getBackgroundFusionSubject,
  isMagicFrameSubject,
  prepareBackgroundFusionSubjectUrl,
  removeBackgroundFusionSubjectFromCanvas,
  removeMagicFrameKeepingSubjectImage,
} from './backgroundFusionSubject';
import {
  handleCreateBackgroundWithAi,
  startTimedLoader,
} from '~core/aiBackground';
import { getState, unsetState } from '~src/state';
import { triggerStateChangedRequest, generateObjRefId } from '~core';
import { onErrorShowMessage } from '~src/errors';
import { trackEvent } from '~src/tracking';
import { FEATURES, isFeatureEnabled } from '~src/features';
import { checkAiCreditsBeforeUse } from '~core/aiCredits';

const BACKGROUND_FUSION_QUALITY = 'medium';
const BACKGROUND_FUSION_REQUIRED_CREDITS = 1;

// Sets the generated image as the canvas background. Replicates the pattern of
// setCanvasBackground in image.js (which is not exported).
const setCanvasBackgroundImage = (imageUrl, layout) =>
  new Promise((resolve) => {
    fabric.Image.fromURL(
      imageUrl,
      (image) => {
        image.set({
          crossOrigin: 'anonymous',
          giotto: {
            objRefId: generateObjRefId('background'),
            isBackground: true,
            isBackgroundFusion: true,
            backgroundFusion: layout,
          },
        });
        window.canvas.setBackgroundImage(
          image,
          window.canvas.renderAll.bind(window.canvas),
          {
            scaleX: window.canvas.width / image.width,
            scaleY: window.canvas.height / image.height,
            originX: 'left',
            originY: 'top',
            crossOrigin: 'anonymous',
            erasable: false,
          }
        );
        resolve(image);
      },
      { crossOrigin: 'anonymous' }
    );
  });

const saveResultToAttachments = async (
  dataUrl,
  { layout, rawBackgroundUrl, rawSubjectUrl }
) =>
  uploadDataUrlToUploader({
    dataUrl,
    uploader: getState('uploader.allegati'),
    customAttachmentProps: {
      isBackgroundFusion: true,
      backgroundFusion: layout,
      backgroundFusionRawBackground: rawBackgroundUrl,
      backgroundFusionRawSubject: rawSubjectUrl,
    },
  });

const uploadMaskToEphemeralStorage = async (dataUrl) =>
  uploadDataUrlToUploader({
    dataUrl,
    uploader: getState('uploader.ephemeral'),
    name: `background-fusion-mask-${Date.now()}.png`,
  });

const getCanvasDimensions = () => ({
  width: Math.round(window.canvas.width),
  height: Math.round(window.canvas.height),
});

const getBackgroundImageDimensions = (backgroundImage, fallbackDimensions) => {
  const element =
    backgroundImage && typeof backgroundImage.getElement === 'function'
      ? backgroundImage.getElement()
      : backgroundImage?._element;
  const width = Math.round(
    backgroundImage?.width ||
      element?.naturalWidth ||
      element?.width ||
      fallbackDimensions.width
  );
  const height = Math.round(
    backgroundImage?.height ||
      element?.naturalHeight ||
      element?.height ||
      fallbackDimensions.height
  );

  if (width > 0 && height > 0) {
    return { width, height };
  }

  return fallbackDimensions;
};

const scalePersonToDimensions = (person, fromDimensions, toDimensions) => {
  const scaleX = toDimensions.width / fromDimensions.width;
  const scaleY = toDimensions.height / fromDimensions.height;

  return {
    ...person,
    left: person.left * scaleX,
    top: person.top * scaleY,
    width: person.width * scaleX,
    height: person.height * scaleY,
  };
};

const MAGIC_FRAME_BACKGROUND_FUSION_ACTIONS = {
  CONTINUE: 'continue',
  REMOVE_AND_CONTINUE: 'remove_and_continue',
  CANCEL: 'cancel',
};

const chooseMagicFrameBackgroundFusionAction = async () => {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Cornice Magica selezionata',
    text:
      'Per un risultato migliore, rimuovi la Cornice Magica e usa la foto originale. Puoi comunque continuare, ma la cornice potrebbe non essere mantenuta correttamente.',
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: 'Continua comunque',
    denyButtonText: 'Rimuovi e continua',
    cancelButtonText: 'Annulla',
    reverseButtons: true,
  });

  if (result.isConfirmed) {
    return MAGIC_FRAME_BACKGROUND_FUSION_ACTIONS.CONTINUE;
  }

  if (result.isDenied) {
    return MAGIC_FRAME_BACKGROUND_FUSION_ACTIONS.REMOVE_AND_CONTINUE;
  }

  return MAGIC_FRAME_BACKGROUND_FUSION_ACTIONS.CANCEL;
};

const prepareGenerationInput = async ({
  bgImage,
  bgUrl,
  personUrl,
  layout,
  canvasDimensions,
}) => {
  const scaledPersonUrl = getScaledPersonReferenceUrl(personUrl, layout.person);
  const maskDimensions = getBackgroundImageDimensions(
    bgImage,
    canvasDimensions
  );
  const maskPerson = scalePersonToDimensions(
    layout.person,
    canvasDimensions,
    maskDimensions
  );
  const maskDataUrl = createBackgroundFusionMaskDataUrl({
    ...maskDimensions,
    person: maskPerson,
  });
  const maskUrl = await uploadMaskToEphemeralStorage(maskDataUrl);

  return {
    imageUrls: [bgUrl, scaledPersonUrl],
    ...canvasDimensions,
    layout,
    maskUrl,
  };
};

export default async function handleBackgroundFusion() {
  const allowed = await isFeatureEnabled(FEATURES.AI_USE_AI_EDIT, false);
  if (!allowed) {
    alert('Questa funzione non è disponibile nel tuo piano di abbonamento');
    return;
  }

  const activeObject = window.canvas.getActiveObject();
  if (!canUseBackgroundFusionSubject(activeObject)) {
    Swal.fire({
      icon: 'warning',
      title: 'Attenzione',
      text: 'Seleziona prima la foto o la Cornice Magica del defunto.',
    });
    return;
  }
  let selection = getBackgroundFusionSubject(activeObject);

  const bgImage = window.canvas.backgroundImage;
  const bgUrl = bgImage ? bgImage.getSrc() : null;

  if (!bgUrl) {
    // No background: ask how to provide one.
    const choice = await Swal.fire({
      icon: 'question',
      title: 'Manca lo sfondo',
      text: 'Per la fusione serve uno sfondo. Cosa vuoi fare?',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: '🖼️ Scegli da libreria',
      denyButtonText: '✨ Crea con IA',
      cancelButtonText: 'Annulla',
    });

    if (choice.isConfirmed) {
      // Library: open the Backgrounds sidebar so the user picks one directly.
      // Same pattern as the `opensidebar` pre-open in sidebarLegacy.js; the
      // guard avoids the same-tab toggle that would close an already open
      // Backgrounds sidebar.
      const $backgroundsBtn = $('#btn-sidebar-backgrounds');
      const isAlreadyOnBackgrounds =
        getState('isSidebarOpen') && $backgroundsBtn.hasClass('active');
      if (!isAlreadyOnBackgrounds) {
        $backgroundsBtn.trigger('click');
      }
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'info',
        title: 'Applica uno sfondo, poi rilancia la Fusione Sfondo',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      });
      return;
    }

    if (choice.isDenied) {
      // Create with AI: open feature ②; the user re-launches the fusion after.
      await handleCreateBackgroundWithAi();
      return;
    }

    if (choice.dismiss !== Swal.DismissReason.cancel) {
      return;
    }
    return;
  }

  if (isMagicFrameSubject(selection)) {
    const magicFrameAction = await chooseMagicFrameBackgroundFusionAction();
    if (magicFrameAction === MAGIC_FRAME_BACKGROUND_FUSION_ACTIONS.CANCEL) {
      return;
    }

    if (
      magicFrameAction ===
      MAGIC_FRAME_BACKGROUND_FUSION_ACTIONS.REMOVE_AND_CONTINUE
    ) {
      unsetState('magicFrameWithPendingPersonDragUpdate');
      unsetState('magicFrameWithPendingPersonDragPreviousData');
      unsetState('pauseMagicFrameDraggingCheck');
      unsetState('itWasOverMagicFrame');

      const imageSelection = removeMagicFrameKeepingSubjectImage(selection);
      if (!imageSelection) {
        Swal.fire({
          icon: 'error',
          title: 'Cornice Magica non rimossa',
          text:
            'Non è stato possibile recuperare la foto dalla Cornice Magica. Rimuovila manualmente e riprova.',
        });
        return;
      }

      selection = imageSelection;
      triggerStateChangedRequest();
    }
  }

  const confirmed = await confirmBackgroundFusionPlacement({
    canvas: window.canvas,
    selection,
  });
  if (!confirmed) {
    return;
  }

  const hasCredits = await checkAiCreditsBeforeUse({
    featureName: 'Fusione Sfondo',
    requiredCredits: BACKGROUND_FUSION_REQUIRED_CREDITS,
  });
  if (!hasCredits) {
    return;
  }

  const startTime = performance.now();
  const loader = startTimedLoader({
    title: '✨ Fusione con lo sfondo in corso',
    messages: [
      { icon: '🖼️', text: 'Analizzando foto e sfondo...' },
      { icon: '🎨', text: 'Fondendo il ritratto nello sfondo...' },
      { icon: '✨', text: 'Armonizzando luci e colori...' },
      { icon: '💎', text: 'Rifinitura finale...' },
    ],
    durationMs: 230000,
  });

  let resultUrl;
  let layout;
  let attachmentMetadata;
  try {
    const canvasDimensions = getCanvasDimensions();
    const background = getBackgroundDescriptor({
      backgroundImage: bgImage,
      backgroundUrl: bgUrl,
      editorConfig: window.editorConfig,
    });

    layout = buildBackgroundFusionLayout({
      canvasWidth: canvasDimensions.width,
      canvasHeight: canvasDimensions.height,
      selection,
      background,
    });

    const personUrl = await prepareBackgroundFusionSubjectUrl(selection);
    if (!personUrl) {
      loader.close();
      return onErrorShowMessage();
    }

    attachmentMetadata = {
      layout,
      rawBackgroundUrl: bgUrl,
      rawSubjectUrl: personUrl,
    };
    const generationInput = await prepareGenerationInput({
      bgImage,
      bgUrl,
      personUrl,
      layout,
      canvasDimensions,
    });

    resultUrl = await generateBackgroundFusion(generationInput);
  } catch (err) {
    console.error(err);
    loader.close();
    trackEvent('Background Fusion', { success: false });
    return onErrorShowMessage();
  }

  // Image is ready → fill the bar to 100% while we save/apply it.
  loader.complete();

  try {
    const canvasDimensions = getCanvasDimensions();
    const exactResultDataUrl = await imageUrlToExactDataUrl(
      resultUrl,
      canvasDimensions
    );
    const newImageUrl = await saveResultToAttachments(
      exactResultDataUrl,
      attachmentMetadata || { layout }
    );

    await setCanvasBackgroundImage(newImageUrl, layout);
    removeBackgroundFusionSubjectFromCanvas(selection);
    window.canvas.discardActiveObject();

    window.canvas.renderAll();
    triggerStateChangedRequest();
    loader.close();

    trackEvent('Background Fusion', {
      success: true,
      time_taken: parseInt(performance.now() - startTime, 10),
      background_kind: layout?.background?.kind,
      quality: BACKGROUND_FUSION_QUALITY,
      required_credits: BACKGROUND_FUSION_REQUIRED_CREDITS,
    });

    Swal.fire({
      icon: 'success',
      title: '✨ Fusione completata!',
      timer: 2500,
      showConfirmButton: false,
    });
  } catch (err) {
    console.error('Error applying background fusion result:', err);
    loader.close();
    const attachmentsUploader = getState('uploader.allegati');
    if (attachmentsUploader) {
      attachmentsUploader.cancelAll();
    }
    trackEvent('Background Fusion', { success: false });
    return onErrorShowMessage();
  }
}
